import React, { createContext, useContext, useEffect, useState } from "react";
import { doc, getDoc, getDocFromServer } from "firebase/firestore";
import { SiteSettings, siteSettingsDefault } from "../model/SiteSettingModel";
import { auth, db, getSiteKey, maindb } from "../firebaseConfig";
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import { Site } from "../model/SiteModel";
import { Utils } from "../common/Utils";

interface GlobalContextType {
  user: User | null;
  isAdmin: boolean;
  userloaded: boolean;
  settingsLoaded: boolean;
  logOut: () => Promise<void>; // Add signOut function to context  
  settings: SiteSettings;
  site: Site | undefined;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(siteSettingsDefault);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userloaded, setUserLoaded] = useState(false);
  const [site, setSite] = useState<Site>();
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {

    const fetchSettings = async () => {
      try {

        const key = getSiteKey();
        if (key) {
          const ref = doc(maindb, "sites", key);
          const siteSnap = await getDoc(ref);
          if (!siteSnap.exists()) {
            return;
          }
          const site = siteSnap.data() as Site;
          setSite(site);
          if (site && !site.siteLoaded) {
            return;
          }
        }

        const storageKey = `orderq-${key}`;
        const data = sessionStorage.getItem(storageKey);
        if (data) {
          const settings = JSON.parse(data) as SiteSettings;
          setSettings(settings);
          return;
        }

        const docRef = doc(db, "website", "settings");
        const docSnap = await getDocFromServer(docRef);
        if (docSnap.exists()) {
          const settings = docSnap.data() as SiteSettings
          setSettings(settings);

          sessionStorage.setItem(storageKey, JSON.stringify(settings));
        }
      }
      catch (error) {
        console.error("Error fetching settings:", error);
      }
      finally {
        setSettingsLoaded(true);
      }
    };

    fetchSettings();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {

      setUser(currentUser);

      auth.currentUser?.getIdTokenResult().then((idTokenResult) => {
        const roles = idTokenResult.claims.roles as string[] | undefined;

        const aliasAdmin = `${Utils.GetAlias()}-admin`;
        const aliasStoreAdmin = `${Utils.GetAlias()}-store-admin`
        const isAdmin = roles?.includes("admin") || roles?.includes(aliasAdmin) || roles?.includes("store-admin") || roles?.includes(aliasStoreAdmin);

        setIsAdmin(!!isAdmin);
      });

      setUserLoaded(true);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
      // Optionally handle error (e.g., show a message)
    }
  };

  return (
    <GlobalContext.Provider value={{ settings, site, user, isAdmin, userloaded, settingsLoaded, logOut}}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobal must be used within a GlobalProvider");
  }
  return context;
};